/**
 * Localized role label reader — Family (Oturum 8, Begüm UX request)
 *
 * Returns the role label in the reader's UI language, falling back to the
 * original `role_label` (the value the user typed in their own native
 * language) if the requested target language hasn't been translated yet.
 *
 * Server-side fanout (Edge Functions family-code-claim + family-code-create
 * call _shared/role-fanout.ts) fills `users.role_label_translations` JSONB
 * asynchronously right after profile insert. There's a brief window where
 * a freshly-joined user has empty translations — during that window we
 * gracefully show the original. After ~5-10 seconds the JSONB is populated.
 *
 * Reader components: ContactList, Home (F2F section), Chat (header),
 * Settings (own profile).
 */

import type { Lang } from '@/types/lang';

interface ProfileWithRoleTranslations {
  role_label: string;
  role_label_translations?: Record<string, string> | null;
}

/**
 * Pick the most appropriate role label for the reader's UI language.
 *
 * Priority:
 *   1. role_label_translations[uiLang] if present + non-empty + not chatter
 *   2. role_label (the original, whatever language the user typed)
 *
 * Defensive chatter check (Oturum 8 23:50 — Begüm bug report):
 * Anthropic Haiku occasionally returns conversational text like "I notice the
 * text is already in Turkish, would you like me to translate..." when source/
 * target lang are mismatched. Backend role-fanout now filters these, but
 * existing bad DB rows may still contain them. Frontend defense ensures UI
 * never displays this chatter even if DB cleanup hasn't happened yet.
 *
 * Never returns empty string — at minimum the original role_label is shown.
 */
export function getLocalizedRoleLabel(
  profile: ProfileWithRoleTranslations,
  uiLang: Lang,
): string {
  const translated = profile.role_label_translations?.[uiLang];
  if (
    translated &&
    translated.trim().length > 0 &&
    !looksLikeChatter(translated)
  ) {
    return translated;
  }
  return profile.role_label;
}

const CHATTER_KEYWORDS = [
  'i notice',
  "i'd",
  'i would',
  'if you',
  'translate from',
  'translation would',
  'sorry',
  'unable to',
  'cannot translate',
  'apologize',
  'the translation',
  'note that',
  '**',
];

/**
 * Heuristic: role labels are short noun phrases (≤100 chars typical).
 * Long output or LLM-apology keywords indicate Anthropic returned conversational
 * chatter instead of a translation.
 */
function looksLikeChatter(s: string): boolean {
  if (s.length > 100) return true;
  const lower = s.toLowerCase();
  return CHATTER_KEYWORDS.some((kw) => lower.includes(kw));
}
